import { useCompaniesStore } from '@/piniaStores/companiesStore'
import { CognitiveRouteLocation } from '@/router/routes'
import { RequiredRole } from '@/router/routes/types'
import Store from '@/stores/'
import populateUser from '@/utils/populateUser'
import { storeToRefs } from 'pinia'
import { NavigationFailure, RouteLocationNormalizedLoaded } from 'vue-router'

export function setDocumentTitle(to: CognitiveRouteLocation): void {
  if (to.params.company) {
    const companyParam = to.params.company as string
    const name = companyParam.charAt(0).toUpperCase() + companyParam.slice(1)
    const pageTitle = to.meta.title || to.matched.find((m) => m.meta.title)?.meta.title
    document.title = `${name} ${pageTitle}`
  } else if (to.meta?.title) {
    document.title = to.meta.title
  }
}

export async function handleRouteSpecificRedirects(to: CognitiveRouteLocation): Promise<string | undefined> {
  switch (to.name) {
    case 'Login':
    case 'Activate User':
      if (Store.getters['User/user']) {
        return '/companies'
      }
      break
    case 'Admin':
      if (Store.getters['User/isAdmin']) {
        return '/admin/companies'
      } else if (Store.getters['User/isDataAnalyst']) {
        return '/admin/queries'
      }
      break
    case 'Comparables':
    case 'Company Comparables':
    case 'Sector Comparables':
    case 'Market Comparables':
    case 'Market Movers':
      if (Store.getters['User/isExperienceAccess']) {
        return '/404'
      }
      break
  }
  return undefined
}

export async function checkFeatureFlags(to: CognitiveRouteLocation): Promise<string | undefined> {
  if (!to.matched.some((m) => m.meta.featureFlagRequired)) {
    return undefined
  }
  if (!Store.getters['FeatureFlags/featureFlagsLoaded']) {
    await Store.dispatch('FeatureFlags/getFeatureFlags')
  }
  if (
    !to.matched.every(
      ({ meta }) =>
        !meta.featureFlagRequired || Store.getters['FeatureFlags/isFeatureFlagEnabled'](meta.featureFlagRequired),
    )
  ) {
    return '/'
  }
  return undefined
}

export function checkTokenRequired(to: CognitiveRouteLocation): string | undefined {
  if (!to.meta.token) {
    return undefined
  }
  return to.query.token ? undefined : '/404'
}

export async function checkAuthentication(to: CognitiveRouteLocation): Promise<string | undefined> {
  if (!to.matched.some((m) => m.meta.requiredRoles || m.meta.optionalRoles)) {
    return
  }

  try {
    await populateUser(to)

    const userGroups = Store.getters['User/userGroups'] as RequiredRole[]
    if (!userGroups && to.path !== '/') {
      return '/'
    }
    let failedRequiredCheck = false
    let failedOptionalCheck = false
    if (to.matched.some((m) => m.meta.requiredRoles)) {
      failedRequiredCheck = to.matched.some(({ meta }) => {
        if (!meta.requiredRoles) {
          return false
        }
        return !meta.requiredRoles.every((role) => userGroups.includes(role))
      })
    }
    if (to.matched.some((m) => m.meta.optionalRoles)) {
      failedOptionalCheck = to.matched.some(({ meta }) => {
        if (!meta.optionalRoles) {
          return false
        }
        return !meta.optionalRoles.some((role) => userGroups.includes(role))
      })
    }
    if (failedRequiredCheck || failedOptionalCheck) {
      return '/'
    }
    if (to.name === 'Cognitive Credit AI' && !Store.getters['User/client']?.chatApp) {
      return '/companies'
    } else if (to.name === 'DealConnect') {
      const companiesStore = useCompaniesStore()
      const { clientHasAnyDealDocs, companiesLoaded } = storeToRefs(companiesStore)

      if (!companiesLoaded.value) {
        await companiesStore.loadCompanies()
      }

      if (!clientHasAnyDealDocs.value) {
        return '/companies'
      }
    }
  } catch (err) {
    console.error(err)
    // if this is a route that allows non-authenticated users
    //  we don't want to redirect to login just because the user
    // data failed to load
    if (!to.meta.optionalRoles?.length && !to.meta.requiredRoles?.length) {
      return
    }
    return `/?redirect=${encodeURIComponent(to.fullPath)}`
  }
}

export const afterEach = async (
  to: RouteLocationNormalizedLoaded,
  from: RouteLocationNormalizedLoaded,
  failure: NavigationFailure | void,
) => {
  if (failure) {
    return
  }
  const isSameRoute = to.path === from.path
  const isSameQuery = JSON.stringify(to.query) === JSON.stringify(from.query)
  const isSameAnchor = to.hash === from.hash
  const { path, hash, fullPath } = to
  if (!(isSameRoute && isSameQuery && isSameAnchor)) {
    const payload = { path }
    const queryString = fullPath.indexOf('?')

    if (hash) {
      payload.anchor = hash
    }

    if (queryString > 0) {
      payload.query = fullPath.slice(queryString + 1, fullPath.length)
    }

    if (Store.getters['User/user']) {
      await Store.dispatch('User/navigate', payload)
    }

    const showRacTour =
      to.meta.title === 'Documents' && Store.getters['User/preferences'].messagePreferences?.showRACUploadHelp

    if (!Store.getters['App/isOnTour'] && !showRacTour) {
      const survey = await Store.dispatch('User/setSurvey')
      if (survey?.showPopup) {
        Store.dispatch('Modal/openModal', { name: 'surveyModal' })
      }
    }
  }
  // clear leaveToRoute if exists
  if (Store.getters['App/leaveToRoute'] && Store.getters['App/leaveToRoute'].path === to.path) {
    await Store.dispatch('App/setLeaveToRoute', null)
  }
}

export const beforeResolve = async (to: CognitiveRouteLocation) => {
  // Handle 404
  if (!to.matched.length) {
    return '/404'
  }
  // Clear Errors from other pages
  if (Store.getters['App/hasError']) {
    await Store.dispatch('App/clearError')
  }
}
